///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ProjectFolderDTO, ProjectFolderEnum } from '../../generated/shared-types.model';
import { isDefined } from '@siklu/sui-lib/utils';

export type RootFoldersInfo = Partial<Record<ProjectFolderEnum, number>>;

@Injectable({
    providedIn: 'root'
})
export class FoldersService {
    private readonly fetchUrl = 'api/folders';
    private readonly modifyUrl = 'api/folders';

    private readonly companyRootFolderIds = new Map<number, RootFoldersInfo>();

    constructor(private readonly http: HttpClient) {}

    getFolder(folderId: number): Observable<ProjectFolderDTO> {
        return this.http.get<ProjectFolderDTO>(`${this.fetchUrl}/${folderId}`).pipe(catchError(() => of(undefined)));
    }

    getFolders(companyId?: number, type?: ProjectFolderEnum): Observable<ProjectFolderDTO[]> {
        let requestUrl: string;
        if (companyId) {
            requestUrl = `${this.fetchUrl}/companies/${companyId}`;
        } else {
            requestUrl = `${this.fetchUrl}`;
        }

        let params: HttpParams = new HttpParams();
        if (type) {
            params = params.set('type', type);
        }

        return this.http.get<ProjectFolderDTO[]>(requestUrl, { params }).pipe(
            map((folders: ProjectFolderDTO[]) => [...folders].sort((a, b) => a.folderOrder - b.folderOrder)),
            tap((folders: ProjectFolderDTO[]) => this.mapCompanyIdToRootFoldersInfo(folders, companyId))
        );
    }

    createFolder(folder: ProjectFolderDTO): Observable<number> {
        return this.http.post<number>(`${this.modifyUrl}`, folder).pipe(catchError(() => of(undefined)));
    }

    updateFolder(folder: ProjectFolderDTO): Observable<number> {
        return this.http.put<number>(`${this.modifyUrl}/${folder.id}`, folder).pipe(catchError(() => of(undefined)));
    }

    deleteFolder(folderId: number): Observable<any> {
        return this.http.delete(`${this.modifyUrl}/${folderId}`).pipe(catchError(() => of(undefined)));
    }

    hasRootFolderIds(companyId?: number): boolean {
        return this.companyRootFolderIds.has(companyId);
    }

    getRootFolderIds(companyId?: number): RootFoldersInfo {
        return this.companyRootFolderIds.get(companyId);
    }

    private mapCompanyIdToRootFoldersInfo(folders: ProjectFolderDTO[], companyId?: number): void {
        const info = {};
        for (const folder of folders) {
            const { id, parentId, type } = folder;
            if (!isDefined(parentId)) {
                info[type] = id;
            }
        }
        this.companyRootFolderIds.set(companyId, info as RootFoldersInfo);
    }
}
